import _ from 'lodash';
import React, { Fragment } from 'react';
import moment from 'moment';
import CheckIcon from '@material-ui/icons/Check';
import ErrorIcon from '@material-ui/icons/Error';

import { valueToAbbreviation } from '../../libs/products';
import { showErrors } from '../../modules/general';

import OpenLink from '@material-ui/icons/OpenInNew';
import PopOver from '../../components/ui/PopOver';

const formatTotal = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 2,
}).format;
const _ft = formatTotal;

const formatCpg = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 5,
}).format;

function formatPercentage(p) {
	if (_.isNil(p)) {
		return 'n/a';
	}

	if (p === '-') {
		return '-';
	}
	return _.round(p * 100, 2) + '%';
}

const _fc = formatCpg;

export function formatStationCogs({ apiSearchResult, isInvoiceSearch, isFreight }) {
	const { classes } = this.props;

	const { collections } = apiSearchResult;
	let apiCogs = [];
	if (isInvoiceSearch) {
		apiCogs = apiSearchResult.stationInvoiceCogs;
	} else {
		apiCogs = apiSearchResult.stationDeliveryCogs;
	}

	if (!apiCogs || !collections) {
		return showErrors(['APISearchResult missing invoice/delivery or Collections']);
	}

	const {
		stationDeliveryCollection,
		stationInvoiceCollection,
		vendorBillCollection,
		stationCollection,
		carrierCollection,
		supplierCollection,
		terminalCollection,
		railcarCollection,
	} = collections;

	const stationLookup = _.keyBy(stationCollection, 'stationId');
	const stationDeliveryLookup = _.keyBy(stationDeliveryCollection, 'stationDeliveryId');
	const stationInvoiceLookup = _.keyBy(stationInvoiceCollection, 'stationInvoiceId');
	const vendorBillLookup = _.keyBy(vendorBillCollection, 'vendorBillId');
	const carrierLookup = _.keyBy(carrierCollection, 'carrierId');
	const supplierLookup = _.keyBy(supplierCollection, 'supplierId');
	const terminalLookup = _.keyBy(terminalCollection, 'terminalId');
	const railcarLookup = _.keyBy(railcarCollection, 'railcarId');

	const ret = [];
	const errors = [];

	apiCogs.forEach((apiCog) => {
		const {
			// Only on invoice searches
			stationInvoiceId,
			stationDeliveryId,
			linkedToStationInvoiceIds,
			linkedToVendorOrderAndBillIds,
			missingInventoryAllocation,
			missingInventoryAllocationCost,

			// totalInvoiceE85LineItemCost,
			totalInvoiceNetGallons,

			totalInvoiceFreightAmount,
			freightInvoicePerGallon,
			totalInvoiceE85Amount,
			totalInvoiceE85PerGallon,
			totalInvoiceE85PlusFreightAmount,
			e85PlusFreightInvoicePerGallon,

			isAllE85,
			e85CostPerGallon,
			e85TotalQuantity,
			e85TotalCost,
			unlCostPerGallon,
			unlTotalQuantity,
			unlTotalCost,
			etohCostPerGallon,
			etohTotalQuantity,
			etohTotalCost,

			freightTotalCost,
			freightCostPerGallon,
			fuelTotalCost,
			fuelCostPerGallon,
			cogs,
			cogsPerGallon,

			freightProfit,
			freightProfitPerGallon,
			fuelProfit,
			fuelProfitPerGallon,
			profit,
			profitPerGallon,

			doesNotHaveAEtohVendorBill,
			noFreightBillButHasZeroFreightCost,

			freightDemurrageTotalCost,
			freightDemurrageCostPerGallon,
			railcarId,
		} = apiCog;

		const stationDelivery = stationDeliveryLookup[stationDeliveryId];
		if (!stationDelivery) {
			return errors.push(`Missing stationDeliveryId:${stationDeliveryId}`);
		}

		//
		//- Format the delivery
		const {
			primaryProductType,
			primaryStationId,
			primaryDeliveryAmount,
			secondaryStationId,
			secondaryDeliveryAmount,
			carrierId,
			deliveryWindowEndDateTime,
		} = stationDelivery;

		const stations = [];
		const primaryStation = stationLookup[primaryStationId] || { number: '?', name: 'Missing' };
		let totalDeliveryAmount = primaryDeliveryAmount;
		let primaryStationDisplay = `${primaryStation.number} - ${primaryStation.name}`;
		if (secondaryStationId) {
			primaryStationDisplay += ` (${primaryDeliveryAmount})`;
		}
		stations.push(primaryStationDisplay);

		if (secondaryStationId) {
			const secondaryStation = stationLookup[secondaryStationId] || {
				number: '?',
				name: 'Missing',
			};
			stations.push(
				`${secondaryStation.number} - ${secondaryStation.name} (${secondaryDeliveryAmount})`
			);
			totalDeliveryAmount += secondaryDeliveryAmount;
		}

		const carrier = carrierLookup[carrierId] || { name: 'Missing' };
		const dateDisplay = moment(deliveryWindowEndDateTime).format('ddd, M/D/YYYY');
		const deliveryDisplay = (
			<>
				<div>
					<a
						href={`/logistics/stationDelivery/update/${stationDeliveryId}`}
						className={classes.linkedItem}
						target="_blank"
						rel="noreferrer noopener"
					>
						<span>Delivery #{stationDeliveryId}</span> <OpenLink className={classes.openLink} />
					</a>
				</div>
				{stations.map((st, i) => {
					return <div key={i}>{st}</div>;
				})}
				<div>{carrier.name}</div>
				<div>{dateDisplay}</div>
			</>
		);

		const row = {
			key: isInvoiceSearch ? stationInvoiceId : stationDeliveryId,
			type: 'stationDelivery',
			deliveryDateSort: deliveryWindowEndDateTime,
			deliveryDay: moment(deliveryWindowEndDateTime).format('YYYY-MM-DD'),
			productType: primaryProductType,
			primaryProductType,
			deliveryDisplay,
			totalDeliveryAmount,
			carrierDisplay: carrier.name,
			// totalInvoiceE85LineItemCost,
			totalInvoiceNetGallons,

			totalInvoiceFreightAmount,
			freightInvoicePerGallon,
			totalInvoiceE85Amount,
			totalInvoiceE85PerGallon,
			totalInvoiceE85PlusFreightAmount,
			e85PlusFreightInvoicePerGallon,

			e85CostPerGallon,
			e85TotalQuantity,
			e85TotalCost,
			unlCostPerGallon,
			unlTotalQuantity,
			unlTotalCost,
			etohCostPerGallon,
			etohTotalQuantity,
			etohTotalCost,

			freightTotalCost,
			freightCostPerGallon,
			fuelTotalCost,
			fuelCostPerGallon,
			cogs,
			cogsPerGallon,
			profit,
			profitPerGallon,

			freightDemurrageTotalCost,
			freightDemurrageCostPerGallon,
		};

		const cogErrors = apiCog.errors;

		const railcar = railcarLookup[railcarId];
		if (railcarId && !railcar) {
			cogErrors.push(`Could not find railcar:${railcarId}`);
		}

		if (railcar) {
			const paddedNumber = _.padStart((1000 - railcar.requestedNumber || 0).toString(), 3, '0');
			const requestedMonthDisplay = moment(railcar.requestedMonth).format('MMM');
			const requestedMonthNumberDisplay = `${requestedMonthDisplay} (#${railcar.requestedNumber})`;
			railcar.railcarSort = `${railcar.requestedMonth}_${paddedNumber}`;

			let linkedDisplay;
			if (railcar.linkedRailcarId) {
				const linkedRailcar = railcarLookup[railcar.linkedRailcarId];
				if (railcarLookup[railcar.linkedRailcarId] && !linkedRailcar) {
					cogErrors.push(`Could not find linked railcar:${railcarId}`);
				}

				if (linkedRailcar) {
					linkedDisplay = linkedRailcar.railcarNumber;
				}
			}

			row.railcarDisplay = (
				<div>
					<div>Railcar #: {railcar.railcarNumber}</div>
					{linkedDisplay && <div>Linked #:{linkedDisplay}</div>}
					<div>Supplier: {railcar.fuelSupplier}</div>
					<div>PO #: {requestedMonthNumberDisplay}</div>
				</div>
			);

			/*
			'fuelSupplier',
			`DATE_FORMAT(requestedMonth, '%Y-%m-%d') as requestedMonth`,
			'requestedNumber',
			'product',
			'railcarNumber',
			'bol',
			'netGallons',
			'grossGallons',
			`DATE_FORMAT(requestedShipDate, '%Y-%m-%d') as requestedShipDate`,
			`DATE_FORMAT(actualShipDate, '%Y-%m-%d') as actualShipDate`,
			`DATE_FORMAT(etaDate, '%Y-%m-%d') as etaDate`,
			`DATE_FORMAT(cpDate, '%Y-%m-%d') as cpDate`,
			`DATE_FORMAT(apDate, '%Y-%m-%d') as apDate`,
			'inTransitDays',
			`DATE_FORMAT(emptyDate, '%Y-%m-%d') as emptyDate`,
			`DATE_FORMAT(relinquishedDate, '%Y-%m-%d') as relinquishedDate`,
			'onSiteDays',
			'linkedRailcarId',
			'currentStatus',
			*/
		}

		if (!_.isEmpty(cogErrors)) {
			cogErrors.forEach((cogError) => {
				if (isInvoiceSearch) {
					errors.push(`Invoice #${stationInvoiceId}: ${cogError}`);
				} else {
					errors.push(`Delivery #${stationDeliveryId}: ${cogError}`);
				}
			});

			const popoverValue = cogErrors.join('.  ');
			row.status = 'error';
			row.statusDisplay = (
				<PopOver value={popoverValue} noJustify={true}>
					<ErrorIcon style={{ color: '#e53935' }} />
				</PopOver>
			);
		} else {
			row.status = 'good';
			row.statusDisplay = (
				<>
					<CheckIcon style={{ color: '#43A047' }} />
				</>
			);
		}

		let stationInvoiceIds = [];
		if (isInvoiceSearch) {
			stationInvoiceIds.push(apiCog.stationInvoiceId);
		} else {
			// No Invoices
			if (_.isEmpty(linkedToStationInvoiceIds)) {
				row.invoiceDisplay = 'No invoice yet';
				// row.invoiceVolumes = 'No Invoice';
				// row.diffDisplay = 'n/a';
				ret.push(row);
				return;
			}
			stationInvoiceIds = linkedToStationInvoiceIds;
		}

		//
		// Format invoice
		const invoiceDisplays = [];
		stationInvoiceIds.forEach((stationInvoiceId) => {
			const stationInvoice = stationInvoiceLookup[stationInvoiceId];
			if (!stationInvoice) {
				return errors.push(`Missing stationInvoice:${stationInvoiceId}`);
			}

			const { products, status, stationId } = stationInvoice;

			//- InvoiceInfo
			const invoiceLines = [];
			if (status !== 'issued') {
				invoiceLines.push(
					<div>
						Status: <strong>{status}</strong>
					</div>
				);
			}

			if (_.isEmpty(products)) {
				invoiceLines.push('No fuels yet');
			} else {
				products.forEach((product) => {
					const { productType, terminalSupplier, netGallons } = product;
					let terminalDisplay = 'No terminal yet';
					let supplierDisplay = 'No supplier yet';
					if (terminalSupplier) {
						const { terminalId, supplierId } = terminalSupplier;
						const terminal = terminalLookup[terminalId] || {
							name: `Missing terminal (${terminalId})`,
						};
						const supplier = supplierLookup[supplierId] || {
							name: `Missing supplier (${supplierId})`,
						};
						terminalDisplay = terminal.name;
						supplierDisplay = supplier.name;
					}
					invoiceLines.push(
						<div className={classes.terminalSupplier}>
							<div>{terminalDisplay}</div>
							<div>
								{netGallons} {valueToAbbreviation(productType)}: {supplierDisplay}
							</div>
						</div>
					);
				});
			}

			if (isInvoiceSearch) {
				const station = stationLookup[stationId] || { number: '?', name: 'Missing' };
				const stationDisplay = `${station.number} - ${station.name}`;
				invoiceLines.push(`Station: ${stationDisplay}`);
			}

			invoiceDisplays.push(
				<div key={stationInvoiceId}>
					<div>
						<a
							href={`/accounting/stationInvoice/update/${stationInvoiceId}`}
							className={classes.linkedItem}
							target="_blank"
							rel="noreferrer noopener"
						>
							<span>Invoice #{stationInvoiceId}</span> <OpenLink className={classes.openLink} />
						</a>
					</div>
					<div style={{ paddingLeft: 10 }}>
						{invoiceLines.map((invoiceLine, i) => {
							return <div key={`il_${i}`}>{invoiceLine}</div>;
						})}
					</div>
				</div>
			);
		});

		row.invoiceDisplay = (
			<>
				{invoiceDisplays.map((invoiceDisplay) => {
					return invoiceDisplay;
				})}
			</>
		);

		if (missingInventoryAllocation || missingInventoryAllocationCost) {
			row.invoiceAmountsDisplay = <div>No inventory allocation yet</div>;
			ret.push(row);
			return;
		}

		row.invoiceAmountsDisplay = (
			<div>
				<div className={classes.fuelGroup}>
					Net Gallons: <span className={classes.value}>{totalInvoiceNetGallons.toString()}</span>
				</div>
				<div className={classes.fuelGroup}>
					<div>
						Inv Freight Amount:{' '}
						<span className={classes.value}>{_ft(totalInvoiceFreightAmount)}</span>
					</div>
					<div>
						Inv Freight CPG: <span className={classes.value}>{_fc(freightInvoicePerGallon)}</span>
					</div>
				</div>
				<div className={classes.fuelGroup}>
					<div>
						Inv E85 Amount: <span className={classes.value}>{_ft(totalInvoiceE85Amount)}</span>
					</div>
					<div>
						Inv E85 CPG: <span className={classes.value}>{_fc(totalInvoiceE85PerGallon)}</span>
					</div>
				</div>
				<div className={classes.fuelGroup}>
					<div>
						Inv E85 + Freight Amount:{' '}
						<span className={classes.value}>{_ft(totalInvoiceE85PlusFreightAmount)}</span>
					</div>
					<div>
						Inv E85 + Freight CPG:{' '}
						<span className={classes.value}>{_fc(e85PlusFreightInvoicePerGallon)}</span>
					</div>
				</div>
			</div>
		);

		//
		// No vendorBills
		if (_.isEmpty(linkedToVendorOrderAndBillIds)) {
			row.vendorBillDisplay = 'No vendor bills yet';
			ret.push(row);
			return;
		}

		//
		// Format invoice
		const vendorBillDisplays = [];
		linkedToVendorOrderAndBillIds.forEach((linkedToVendorOrderAndBillId, billIndex) => {
			const {
				vendorBillId,
				vendorBillOrderId,
				productType,
				percentageOfBillUsed,
			} = linkedToVendorOrderAndBillId;
			if (!vendorBillId) {
				console.log('NO vendor BIllId', linkedToVendorOrderAndBillId);
				return;
			}

			const vendorBill = vendorBillLookup[vendorBillId];
			if (!vendorBill) {
				return errors.push(`Missing vendorBill:${vendorBillId}`);
			}

			const { typeOfBill, vendorBillOrders, status } = vendorBill;

			let vendorBillOrder;
			let vendorBillOrderNumber = 0;
			vendorBillOrders.forEach((vbo, i) => {
				if (vendorBillOrderId === vbo.vendorBillOrderId) {
					vendorBillOrderNumber = i;
					vendorBillOrder = vbo;
				}
			});

			if (!vendorBillOrder) {
				return errors.push(`Missing vendorBillOrder:${vendorBillOrderId} on vendorBill`);
			}

			vendorBillDisplays.push(
				<div
					key={`${vendorBillId}_${vendorBillOrderId}_${vendorBillOrderNumber}_${billIndex}`}
					style={{ marginBottom: 5 }}
				>
					<div>
						<a
							href={`/billing/vendorBill/update/${vendorBillId}`}
							className={classes.linkedItem}
							target="_blank"
							rel="noreferrer noopener"
							style={{ whiteSpace: 'nowrap' }}
						>
							<span>
								{_.capitalize(typeOfBill)} Bill #{vendorBillId}
							</span>{' '}
							<OpenLink className={classes.openLink} />
						</a>
					</div>
					<div style={{ paddingLeft: 10 }}>
						{typeOfBill === 'fuel' && (
							<div>
								Fuel: {valueToAbbreviation(productType)} Order #: {vendorBillOrderNumber + 1}
							</div>
						)}
						{percentageOfBillUsed && percentageOfBillUsed !== 1 && (
							<div>% Used: {formatPercentage(percentageOfBillUsed)}</div>
						)}
					</div>

					{status !== 'completed' && (
						<div style={{ paddingLeft: 10 }}>
							<div>
								<strong>Status: {_.capitalize(status)}</strong>
							</div>
						</div>
					)}
				</div>
			);
		});

		if (doesNotHaveAEtohVendorBill) {
			vendorBillDisplays.push(
				<div key={'rtcFuels'} style={{ marginBottom: 5 }}>
					<div>No ETOH Bill (from inventory)</div>
				</div>
			);
		}

		if (noFreightBillButHasZeroFreightCost) {
			vendorBillDisplays.push(
				<div key={'noFreightBillButHasZeroFreightCost'} style={{ marginBottom: 5 }}>
					<div>No Freight Bill</div>
					<div style={{ marginLeft: 10 }}>✓ station has zero freight cost</div>
				</div>
			);
		}

		row.vendorBillDisplay = (
			<>
				{vendorBillDisplays.map((vendorBillDisplay) => {
					return vendorBillDisplay;
				})}
			</>
		);

		let fuelDisplay;
		if (isAllE85) {
			fuelDisplay = (
				<div className={classes.fuelGroup}>
					<div>E85 Cost: {_ft(e85TotalCost)}</div>
					<div>E85 Quantity: {e85TotalQuantity}</div>
					<div>E85 CPG: {_fc(e85CostPerGallon)}</div>
				</div>
			);
		} else {
			fuelDisplay = (
				<>
					<div className={classes.fuelGroup}>
						<div>UNL Cost: {_ft(unlTotalCost)}</div>
						<div>UNL Quantity: {unlTotalQuantity}</div>
						<div>UNL CPG: {_fc(unlCostPerGallon)}</div>
					</div>

					<div className={classes.fuelGroup}>
						<div>ETOH Cost: {_ft(etohTotalCost)}</div>
						<div>ETOH Quantity: {etohTotalQuantity}</div>
						<div>ETOH CPG: {_fc(etohCostPerGallon)}</div>
					</div>
				</>
			);
		}

		row.fuelCogsDisplay = (
			<div>
				{fuelDisplay}
				<div className={classes.fuelGroup}>
					<div>Total Fuel Cost: {_ft(fuelTotalCost)}</div>
					<div>Total Fuel CPG: {_fc(fuelCostPerGallon)}</div>
				</div>
			</div>
		);

		const label = isInvoiceSearch ? 'Invoice' : 'Delivery';
		row.cogsDisplay = (
			<div>
				<div className={classes.fuelGroup}>
					<div>Freight Cost: {_ft(freightTotalCost)}</div>
					<div>Freight CPG: {_fc(freightCostPerGallon)}</div>
				</div>

				{!isFreight && (
					<div className={classes.fuelGroup}>
						<div>
							{label} Cost: {_ft(cogs)}
						</div>
						<div>
							{label} CPG: {_fc(cogsPerGallon)}
						</div>
					</div>
				)}
			</div>
		);

		if (isFreight) {
			row.demurrageDisplay = (
				<div className={classes.fuelGroup}>
					<div>Demurrage Cost: {_ft(freightDemurrageTotalCost)}</div>
					<div>Demurrage CPG: {_fc(freightDemurrageCostPerGallon)}</div>
				</div>
			);
		}

		row.profitsDisplay = (
			<div>
				<div className={classes.fuelGroup}>
					<div>Profit Freight: {_ft(freightProfit)}</div>
					<div>Profit Freight CPG: {_fc(freightProfitPerGallon)}</div>
				</div>

				<div className={classes.fuelGroup}>
					<div>Profit Fuel: {_ft(fuelProfit)}</div>
					<div>Profit Fuel CPG: {_fc(fuelProfitPerGallon)}</div>
				</div>

				<div className={classes.fuelGroup}>
					<div>
						Profit {label}: {_ft(profit)}
					</div>
					<div>
						Profit {label} CPG: {_fc(profitPerGallon)}
					</div>
				</div>
			</div>
		);
		ret.push(row);
	});
	return { cogs: ret, errors };
}

export function formatBulkCogs({ apiSearchResult, isInvoiceSearch, isFreight }) {
	const { collections } = apiSearchResult;
	let apiCogs = [];
	if (isInvoiceSearch) {
		apiCogs = apiSearchResult.bulkInvoiceCogs;
	} else {
		apiCogs = apiSearchResult.bulkDeliveryCogs;
	}

	const { classes } = this.props;

	if (!apiCogs || !collections) {
		return showErrors(['APISearchResult missing bulkDelivery/InvoiceCogs or Collections']);
	}

	const {
		bulkDeliveryCollection,
		bulkInvoiceCollection,
		vendorBillCollection,
		bulkCustomerCollection,
		carrierCollection,
		// supplierCollection,
		rackTransferCollection,
		terminalCollection,
	} = collections;

	const bulkCustomerLookup = _.keyBy(bulkCustomerCollection, 'bulkCustomerId');
	const bulkDeliveryLookup = _.keyBy(bulkDeliveryCollection, 'bulkDeliveryId');
	const bulkInvoiceLookup = _.keyBy(bulkInvoiceCollection, 'bulkInvoiceId');
	const rackTransferLookup = _.keyBy(rackTransferCollection, 'rackTransferId');
	const vendorBillLookup = _.keyBy(vendorBillCollection, 'vendorBillId');
	const carrierLookup = _.keyBy(carrierCollection, 'carrierId');
	// const supplierLookup = _.keyBy(supplierCollection, 'supplierId');
	const terminalLookup = _.keyBy(terminalCollection, 'terminalId');

	const ret = [];
	const errors = [];
	apiCogs.forEach((apiCog) => {
		const {
			bulkInvoiceId,
			bulkDeliveryId,
			linkedToVendorOrderAndBillIds,

			hasInvoiceNotIssued,
			hasRackTransferNotComplete,
			// missingInventoryAllocation,
			// missingInventoryAllocationCost,

			// totalBillAmount,
			// totalBillNetGallons,

			totalInvoiceNetGallons,
			totalInvoiceFreightAmount,
			freightInvoicePerGallon,
			totalInvoiceEtohAmount,
			totalInvoiceEtohPerGallon,
			totalInvoiceEtohPlusFreightAmount,
			etohPlusFreightInvoicePerGallon,

			etohCostPerGallon,
			etohTotalQuantity,
			etohTotalCost,
			freightTotalCost,
			freightCostPerGallon,
			fuelTotalCost,
			fuelCostPerGallon,
			cogs,
			cogsPerGallon,

			freightProfit,
			freightProfitPerGallon,
			fuelProfit,
			fuelProfitPerGallon,
			profit,
			profitPerGallon,

			doesNotHaveAEtohVendorBill,
			isTankTransfer,
			noFreightBillButItsSeaport,
			freightDemurrageTotalCost,
			freightDemurrageCostPerGallon,
		} = apiCog;

		const bulkDelivery = bulkDeliveryLookup[bulkDeliveryId];
		if (!bulkDelivery) {
			return errors.push(`Missing bulkDeliveryId:${bulkDeliveryId}`);
		}

		const { deliveryBulkCustomerId, productType, deliveryDate, carrierId } = bulkDelivery;

		const bulkCustomer = bulkCustomerLookup[deliveryBulkCustomerId] || { name: 'Missing' };
		const bulkCustomerDisplay = bulkCustomer.name;
		const carrier = carrierLookup[carrierId] || { name: 'Missing' };

		//- Delivery
		const dateDisplay = moment(deliveryDate).format('ddd, M/D');
		const deliveryDisplay = (
			<Fragment>
				<div>
					<a
						href={`/logistics/bulkDelivery/update/${bulkDeliveryId}`}
						className={classes.linkedItem}
						target="_blank"
						rel="noreferrer noopener"
					>
						<span>Delivery #{bulkDeliveryId}</span> <OpenLink className={classes.openLink} />
					</a>
				</div>
				<div>{bulkCustomerDisplay}</div>
				<div>{carrier.name}</div>
				<div>{dateDisplay}</div>
			</Fragment>
		);

		const totalDeliveryAmount = 8000;
		const row = {
			key: isInvoiceSearch ? bulkInvoiceId : bulkDeliveryId,
			type: 'bulkDelivery',
			deliveryDateSort: deliveryDate,
			deliveryDay: moment(deliveryDate).format('YYYY-MM-DD'),
			productType,
			deliveryDisplay,
			totalDeliveryAmount,
			carrierDisplay: carrier.name,
			// totalInvoiceE85LineItemCost,
			totalInvoiceNetGallons,

			totalInvoiceFreightAmount,
			freightInvoicePerGallon,
			totalInvoiceEtohAmount,
			totalInvoiceEtohPerGallon,
			totalInvoiceEtohPlusFreightAmount,
			etohPlusFreightInvoicePerGallon,

			etohCostPerGallon,
			etohTotalQuantity,
			etohTotalCost,

			freightTotalCost,
			freightCostPerGallon,
			fuelTotalCost,
			fuelCostPerGallon,
			cogs,
			cogsPerGallon,
			profit,
			profitPerGallon,
			freightDemurrageTotalCost,
			freightDemurrageCostPerGallon,
		};

		const deliveryErrors = apiCog.errors;

		if (!_.isEmpty(deliveryErrors)) {
			deliveryErrors.forEach((deliveryError) => {
				if (isInvoiceSearch) {
					errors.push(`Invoice #${bulkInvoiceId}: ${deliveryError}`);
				} else {
					errors.push(`Delivery #${bulkDeliveryId}: ${deliveryError}`);
				}
			});

			const popoverValue = deliveryErrors.join('.  ');
			row.status = 'error';
			row.statusDisplay = (
				<PopOver value={popoverValue} noJustify={true}>
					<ErrorIcon style={{ color: '#e53935' }} />
				</PopOver>
			);
		} else {
			row.status = 'good';
			row.statusDisplay = (
				<>
					<CheckIcon style={{ color: '#43A047' }} />
				</>
			);
		}

		if (isTankTransfer) {
			if (hasRackTransferNotComplete) {
				row.invoiceDisplay = 'Has rack transfer not complete';
				ret.push(row);
				return;
			}

			makeRackTransferDisplays({
				apiCog,
				row,
				isInvoiceSearch,
				classes,
				rackTransferLookup,
				errors,
				terminalLookup,
				carrier,
			});
		} else {
			if (hasInvoiceNotIssued) {
				row.invoiceDisplay = 'Has invoices not issued';
				ret.push(row);
				return;
			}

			const isAllDone = makeInvoiceDisplays({
				apiCog,
				row,
				isInvoiceSearch,
				classes,
				bulkInvoiceLookup,
				errors,
				terminalLookup,
				carrier,
			});
			if (isAllDone) {
				ret.push(row);
				return;
			}
		}

		//
		// No vendorBills
		if (_.isEmpty(linkedToVendorOrderAndBillIds)) {
			row.vendorBillDisplay = 'No vendor bills yet';
			ret.push(row);
			return;
		}

		//
		// Format invoice
		const vendorBillDisplays = [];
		linkedToVendorOrderAndBillIds.forEach((linkedToVendorOrderAndBillId, billIndex) => {
			const { vendorBillId, vendorBillOrderId, productType } = linkedToVendorOrderAndBillId;
			if (!vendorBillId) {
				return;
			}

			const vendorBill = vendorBillLookup[vendorBillId];
			if (!vendorBill) {
				return errors.push(`Missing vendorBill:${vendorBillId}`);
			}

			const { typeOfBill, vendorBillOrders, status } = vendorBill;

			let vendorBillOrder;
			let vendorBillOrderNumber = 0;
			vendorBillOrders.forEach((vbo, i) => {
				if (vendorBillOrderId === vbo.vendorBillOrderId) {
					vendorBillOrderNumber = i;
					vendorBillOrder = vbo;
				}
			});

			if (!vendorBillOrder) {
				return errors.push(`Missing vendorBillOrder:${vendorBillOrderId} on vendorBill`);
			}

			vendorBillDisplays.push(
				<div
					key={`${vendorBillId}_${vendorBillOrderId}_${vendorBillOrderNumber}_${billIndex}`}
					style={{ marginBottom: 5 }}
				>
					<div>
						<a
							href={`/billing/vendorBill/update/${vendorBillId}`}
							className={classes.linkedItem}
							target="_blank"
							rel="noreferrer noopener"
							style={{ whiteSpace: 'nowrap' }}
						>
							<span>
								{_.capitalize(typeOfBill)} Bill #{vendorBillId}{' '}
							</span>
							<OpenLink className={classes.openLink} />
						</a>
					</div>
					<div style={{ paddingLeft: 10 }}>
						{typeOfBill === 'fuel' && (
							<div>
								Fuel: {valueToAbbreviation(productType)} Order #: {vendorBillOrderNumber + 1}
							</div>
						)}
					</div>
					{status !== 'completed' && (
						<div style={{ paddingLeft: 10 }}>
							<div>
								<strong>Status: {_.capitalize(status)}</strong>
							</div>
						</div>
					)}
				</div>
			);
		});

		if (doesNotHaveAEtohVendorBill) {
			vendorBillDisplays.push(
				<div key={`${bulkDeliveryId}_rtcFuels`} style={{ marginBottom: 5 }}>
					<div>No ETOH Bill (from inventory)</div>
				</div>
			);
		}

		if (noFreightBillButItsSeaport) {
			vendorBillDisplays.push(
				<div key={'noFreightBillButItsSeaport'} style={{ marginBottom: 5 }}>
					<div>No Freight Bill</div>
					<div style={{ marginLeft: 10 }}>✓ Carrier is Seaport</div>
				</div>
			);
		}

		row.vendorBillDisplay = (
			<>
				{vendorBillDisplays.map((vendorBillDisplay) => {
					return vendorBillDisplay;
				})}
			</>
		);

		const fuelDisplay = (
			<div className={classes.fuelGroup}>
				<div>ETOH Cost: {_ft(etohTotalCost)}</div>
				<div>ETOH Quantity: {etohTotalQuantity}</div>
				<div>ETOH CPG: {_fc(etohCostPerGallon)}</div>
			</div>
		);

		row.fuelCogsDisplay = (
			<div>
				{fuelDisplay}
				<div className={classes.fuelGroup}>
					<div>Total Fuel Cost: {_ft(fuelTotalCost)}</div>
					<div>Total Fuel CPG: {_fc(fuelCostPerGallon)}</div>
				</div>
			</div>
		);

		const label = isInvoiceSearch ? 'Invoice' : 'Delivery';
		row.cogsDisplay = (
			<div>
				<div className={classes.fuelGroup}>
					<div>Freight Cost: {_ft(freightTotalCost)}</div>
					<div>Freight CPG: {_fc(freightCostPerGallon)}</div>
				</div>

				<div className={classes.fuelGroup}>
					<div>
						{label} Cost: {_ft(cogs)}
					</div>
					<div>
						{label} CPG: {_fc(cogsPerGallon)}
					</div>
				</div>
			</div>
		);

		if (isFreight) {
			row.demurrageDisplay = (
				<div className={classes.fuelGroup}>
					<div>Demurrage Cost: {_ft(freightDemurrageTotalCost)}</div>
					<div>Demurrage CPG: {_fc(freightDemurrageCostPerGallon)}</div>
				</div>
			);
		}

		row.profitsDisplay = (
			<div>
				<div className={classes.fuelGroup}>
					<div>Profit Freight: {_ft(freightProfit)}</div>
					<div>Profit Freight CPG: {_fc(freightProfitPerGallon)}</div>
				</div>

				<div className={classes.fuelGroup}>
					<div>Profit Fuel: {_ft(fuelProfit)}</div>
					<div>Profit Fuel CPG: {_fc(fuelProfitPerGallon)}</div>
				</div>

				<div className={classes.fuelGroup}>
					<div>
						Profit {label}: {_ft(profit)}
					</div>
					<div>
						Profit {label} CPG: {_fc(profitPerGallon)}
					</div>
				</div>
			</div>
		);

		ret.push(row);
	});
	return { cogs: ret, errors };
}

function makeInvoiceDisplays({
	apiCog,
	row,
	isInvoiceSearch,
	classes,
	bulkInvoiceLookup,
	errors,
	terminalLookup,
	carrier,
}) {
	const { bulkInvoiceId, missingInventoryAllocation, missingInventoryAllocationCost } = apiCog;
	let linkedToBulkInvoiceIds = apiCog.linkedToBulkInvoiceIds;

	const {
		productType,
		totalInvoiceNetGallons,

		totalInvoiceFreightAmount,
		freightInvoicePerGallon,
		totalInvoiceEtohAmount,
		totalInvoiceEtohPerGallon,
		totalInvoiceEtohPlusFreightAmount,
		etohPlusFreightInvoicePerGallon,
	} = row;

	// No Invoices
	if (!isInvoiceSearch) {
		if (_.isEmpty(linkedToBulkInvoiceIds)) {
			row.invoiceDisplay = 'No invoice yet';
			return false;
		}
	} else {
		linkedToBulkInvoiceIds = [bulkInvoiceId];
	}

	//
	// Format invoice
	const invoiceVolumeByType = {};
	const invoiceDisplays = [];
	linkedToBulkInvoiceIds.forEach((bulkInvoiceId) => {
		const bulkInvoice = bulkInvoiceLookup[bulkInvoiceId];
		if (!bulkInvoice) {
			return errors.push(`Missing bulkInvoice:${bulkInvoiceId}`);
		}

		const {
			pickupTerminalId,
			offloadTerminalId,
			pickupNetGallons,
			offloadNetGallons,
		} = bulkInvoice;

		let fuelInfo;
		if (pickupNetGallons && offloadNetGallons) {
			invoiceVolumeByType[productType] = pickupNetGallons;
			const pickupTerminal = terminalLookup[pickupTerminalId] || { name: `Missing terminal` };
			const offloadTerminal = terminalLookup[offloadTerminalId] || { name: `Missing terminal` };
			fuelInfo = (
				<Fragment>
					<div>{carrier.name}</div>
					<div>
						Pickup: {pickupNetGallons} from {pickupTerminal.name}
					</div>
					<div>
						Offload: {offloadNetGallons} to {offloadTerminal.name}
					</div>
				</Fragment>
			);
		} else {
			fuelInfo = (
				<Fragment>
					<div>No documents yet</div>
				</Fragment>
			);
		}

		invoiceDisplays.push(
			<div key={bulkInvoiceId}>
				<div>
					<a
						href={`/accounting/bulkInvoice/update/${bulkInvoiceId}`}
						className={classes.linkedItem}
						target="_blank"
						rel="noreferrer noopener"
					>
						<span>Invoice #{bulkInvoiceId}</span> <OpenLink className={classes.openLink} />
					</a>
				</div>
				<div style={{ paddingLeft: 10 }}>{fuelInfo}</div>
			</div>
		);
	});

	row.invoiceDisplay = (
		<>
			{invoiceDisplays.map((invoiceDisplay) => {
				return invoiceDisplay;
			})}
		</>
	);

	if (missingInventoryAllocation || missingInventoryAllocationCost) {
		row.invoiceAmountsDisplay = <div>No inventory allocation yet</div>;
		return false;
	}

	row.invoiceAmountsDisplay = (
		<div>
			<div className={classes.fuelGroup}>
				Net Gallons:<span className={classes.value}>{totalInvoiceNetGallons.toString()}</span>
			</div>
			<div className={classes.fuelGroup}>
				<div>
					Inv Freight Amount:{' '}
					<span className={classes.value}>{_ft(totalInvoiceFreightAmount)}</span>
				</div>
				<div>
					Inv Freight CPG: <span className={classes.value}>{_fc(freightInvoicePerGallon)}</span>
				</div>
			</div>
			<div className={classes.fuelGroup}>
				<div>
					Inv ETOH Amount: <span className={classes.value}>{_ft(totalInvoiceEtohAmount)}</span>
				</div>
				<div>
					Inv ETOH CPG: <span className={classes.value}>{_fc(totalInvoiceEtohPerGallon)}</span>
				</div>
			</div>
			<div className={classes.fuelGroup}>
				<div>
					Inv ETOH + Freight Amount:
					<span className={classes.value}> {_ft(totalInvoiceEtohPlusFreightAmount)}</span>
				</div>
				<div>
					Inv ETOH + Freight CPG:
					<span className={classes.value}> {_fc(etohPlusFreightInvoicePerGallon)}</span>
				</div>
			</div>
		</div>
	);
}

function makeRackTransferDisplays({
	apiCog,
	row,
	isInvoiceSearch,
	rackTransferLookup,
	classes,
	errors,
	terminalLookup,
	carrier,
}) {
	const { rackTransferId, missingInventoryAllocation, missingInventoryAllocationCost } = apiCog;
	let linkedToRackTransferIds = apiCog.linkedToRackTransferIds;

	const { productType, totalInvoiceNetGallons } = row;

	// No Invoices
	if (!isInvoiceSearch) {
		if (_.isEmpty(linkedToRackTransferIds)) {
			row.invoiceDisplay = 'No rack transfer yet';
			return false;
		}
	} else {
		linkedToRackTransferIds = [rackTransferId];
	}

	//
	// Format invoice
	const invoiceVolumeByType = {};
	const invoiceDisplays = [];
	linkedToRackTransferIds.forEach((rackTransferId) => {
		const rackTransfer = rackTransferLookup[rackTransferId];
		if (!rackTransfer) {
			return errors.push(`Missing rackTransferId:${rackTransferId}`);
		}

		const {
			pickupTerminalId,
			offloadTerminalId,
			pickupNetGallons,
			offloadNetGallons,
		} = rackTransfer;

		let fuelInfo;
		if (pickupNetGallons && offloadNetGallons) {
			invoiceVolumeByType[productType] = pickupNetGallons;
			const pickupTerminal = terminalLookup[pickupTerminalId] || { name: `Missing terminal` };
			const offloadTerminal = terminalLookup[offloadTerminalId] || { name: `Missing terminal` };
			fuelInfo = (
				<Fragment>
					<div>{carrier.name}</div>
					<div>
						Pickup: {pickupNetGallons} from {pickupTerminal.name}
					</div>
					<div>
						Offload: {offloadNetGallons} to {offloadTerminal.name}
					</div>
				</Fragment>
			);
		} else {
			fuelInfo = (
				<Fragment>
					<div>No documents yet</div>
				</Fragment>
			);
		}

		invoiceDisplays.push(
			<div key={rackTransferId}>
				<div>
					<a
						href={`/accounting/rackTransfer/update/${rackTransferId}`}
						className={classes.linkedItem}
						target="_blank"
						rel="noreferrer noopener"
					>
						<span>Rack Transfer #{rackTransferId}</span> <OpenLink className={classes.openLink} />
					</a>
				</div>
				<div style={{ paddingLeft: 10 }}>{fuelInfo}</div>
			</div>
		);
	});

	row.invoiceDisplay = (
		<>
			{invoiceDisplays.map((invoiceDisplay) => {
				return invoiceDisplay;
			})}
		</>
	);

	if (missingInventoryAllocation || missingInventoryAllocationCost) {
		row.invoiceAmountsDisplay = <div>No inventory allocation yet</div>;
		return false;
	}

	row.invoiceAmountsDisplay = (
		<div>
			<div className={classes.fuelGroup}>
				Pickup Net Gallons:
				<span className={classes.value}>{totalInvoiceNetGallons.toString()}</span>
			</div>
		</div>
	);
}
